/**
 * The voice call's events, read and written. Kept free of React and of the
 * app's path aliases so it can be tested on its own.
 *
 * Events follow the OpenAI Realtime API: the model's tool calls arrive in
 * `response.done`; a tool's result goes back as a `function_call_output` item,
 * followed by `response.create` so the model speaks again.
 */

import type { PageContext } from '../assistant/session'

export type VoiceStatus =
  | 'idle'
  | 'connecting'
  /** The line dropped (a locked phone, lost signal); a new one is being opened. */
  | 'reconnecting'
  | 'listening'
  | 'thinking'
  | 'speaking'
  | 'error'

export interface VoiceFunctionCall {
  callId: string
  name: string
  arguments: string
}

export interface VoiceUpdate {
  status?: VoiceStatus
  /** A finished line of transcript, keyed by the conversation item it belongs to. */
  line?: { id: string; role: 'user' | 'assistant'; text: string }
  /** The response a finished assistant line belongs to, when the server said. */
  responseId?: string
  /** A response Helm asked for as the opening greeting has started. */
  greetingResponseId?: string
  calls?: VoiceFunctionCall[]
  error?: string
}

/** Marks the opening greeting, so its line is shown but not kept in the thread. */
const GREETING = 'greeting'

type ServerEvent = { type?: string } & Record<string, unknown>

function str(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

export function readServerEvent(event: ServerEvent): VoiceUpdate {
  switch (event.type) {
    case 'input_audio_buffer.speech_started':
      return { status: 'listening' }
    case 'input_audio_buffer.speech_stopped':
      return { status: 'thinking' }
    case 'response.created': {
      const response = (event.response ?? {}) as { id?: unknown; metadata?: { helm?: unknown } }
      const id = str(response.id)
      return id && response.metadata?.helm === GREETING
        ? { status: 'thinking', greetingResponseId: id }
        : { status: 'thinking' }
    }
    case 'output_audio_buffer.started':
    case 'response.output_audio_transcript.delta':
      return { status: 'speaking' }
    case 'output_audio_buffer.stopped':
      return { status: 'listening' }
    case 'conversation.item.input_audio_transcription.completed': {
      const text = str(event.transcript).trim()
      return text ? { line: { id: str(event.item_id), role: 'user', text } } : {}
    }
    case 'response.output_audio_transcript.done': {
      const text = str(event.transcript).trim()
      if (!text) return {}
      const line = { id: str(event.item_id), role: 'assistant' as const, text }
      const responseId = str(event.response_id)
      return responseId ? { line, responseId } : { line }
    }
    case 'response.done': {
      const response = (event.response ?? {}) as { output?: unknown[]; status?: string }
      const calls = (Array.isArray(response.output) ? response.output : [])
        .filter(
          (item): item is Record<string, unknown> =>
            Boolean(item) &&
            typeof item === 'object' &&
            (item as { type?: unknown }).type === 'function_call',
        )
        .map((item) => ({
          callId: str(item.call_id),
          name: str(item.name),
          arguments: str(item.arguments) || '{}',
        }))
        .filter((call) => call.callId && call.name)
      return calls.length ? { calls, status: 'thinking' } : {}
    }
    case 'error': {
      const error = (event.error ?? {}) as { message?: unknown }
      return { error: str(error.message) || 'The voice service reported an error' }
    }
    default:
      return {}
  }
}

/** Hands a tool's result back to the model. */
export function functionCallOutput(callId: string, output: string) {
  return {
    type: 'conversation.item.create',
    item: { type: 'function_call_output', call_id: callId, output },
  }
}

/** Asks the model to respond, after tool results or a note. */
export function responseCreate() {
  return { type: 'response.create' }
}

/**
 * Has the model speak first when a call opens, so it does not start in
 * silence. The Realtime API's own way: a `response.create` before the person
 * has said anything, with instructions for that one response. Tools are off
 * for it, and it is tagged so its line is not kept as part of the record.
 */
export function greeting() {
  return {
    type: 'response.create',
    response: {
      instructions:
        'The call has just connected. Greet the person in a few words to show you are listening, such as "Ready when you are." Do not ask how you can help at length, do not introduce yourself, and do not call any tools.',
      tool_choice: 'none',
      metadata: { helm: GREETING },
    },
  }
}

/**
 * Tells the model what the person did with a change it put on screen, so it
 * can say so out loud. Sent as a system item: it is Helm speaking, not them.
 */
export function approvalNote(summary: string, approved: boolean, failure?: string | null) {
  const text = approved
    ? failure
      ? `The person approved "${summary}" on screen, but it failed: ${failure}. Tell them briefly.`
      : `The person approved "${summary}" on screen and it is done. Confirm it briefly.`
    : `The person rejected "${summary}" on screen. Nothing was changed. Acknowledge it briefly.`
  return {
    type: 'conversation.item.create',
    item: { type: 'message', role: 'system', content: [{ type: 'input_text', text }] },
  }
}

/** A record page in words the model can act on; a plain route otherwise. */
export function describePage(page: PageContext): string {
  return page.kind && page.id
    ? `the ${page.kind} ${page.id} (${page.path})`
    : `the ${page.path} page`
}

/**
 * Tells the model, quietly, that the person moved to another page during the
 * call, so "this one" means what is on screen now. A system item with no
 * `response.create`: it is context, not something to answer.
 */
export function pageNote(page: PageContext) {
  const text = `The person now has ${describePage(page)} open in Helm. If they say "this" or "here", they mean it; read it with your tools before answering. Do not mention this note.`
  return {
    type: 'conversation.item.create',
    item: { type: 'message', role: 'system', content: [{ type: 'input_text', text }] },
  }
}
